import ContentFilter from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, ListSubheader, Menu, MenuItem, styled, TextField } from '@mui/material';
import { get, isEqual } from 'lodash';
import {
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  AddSavedQueryDialog,
  extractValidSavedQueries,
  FilterButtonMenuItem,
  FilterButtonProps,
  FilterContext,
  FilterContextType,
  getFieldLabelTranslationArgs,
  // The add filter button does not require permissions
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button as RaButton,
  RemoveSavedQueryDialog,
  SavedQuery,
  useListContext,
  useResourceContext,
  useSavedQueries,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { translationToText } from '@sh4res/bo-common/utils/createAccessibleI18nProvider';
import { fuzzyFilter } from '@sh4res/bo-common/utils/fuzzy_search';

/**
 * Menus with more hidden filters than this get a search field on top.
 */
export const FILTER_SEARCH_THRESHOLD = 8;

/**
 * Same query string as react-admin builds with `query-string`, which skips undefined values.
 */
const buildSavedQuerySearch = ({ value }: SavedQuery) => {
  const params = new URLSearchParams({ page: '1' });

  if (value.filter !== undefined) {
    params.set('filter', JSON.stringify(value.filter));
  }
  if (value.displayedFilters !== undefined) {
    params.set('displayedFilters', JSON.stringify(value.displayedFilters));
  }
  if (value.sort) {
    params.set('sort', value.sort.field);
    params.set('order', value.sort.order);
  }
  if (value.perPage !== undefined) {
    params.set('perPage', String(value.perPage));
  }

  return params.toString();
};

/**
 * Drop-in replacement for react-admin's `FilterButton` (forked from ra-ui-materialui 4.16.2) that
 * adds a search field to the "add filter" menu when a list has many filters.
 *
 * The markup of the button and of the menu items is unchanged (`.add-filter`, `.new-filter-item`,
 * `data-key`), so e2e selectors written against react-admin's version keep working.
 */
export const FilterButton = (props: FilterButtonProps) => {
  const { filters: filtersProp, className, disableSaveQuery, ...rest } = props;
  // FilterContext is typed as always set, but it is undefined outside of a List with filters.
  const filtersFromContext = useContext(FilterContext) as FilterContextType | undefined;
  const filters = (filtersFromContext ?? filtersProp) as ReactElement[] | undefined;
  const resource = useResourceContext(props);
  const translate = useTranslate();
  const [savedQueries] = useSavedQueries(resource);
  const navigate = useNavigate();
  const {
    displayedFilters = {},
    filterValues,
    perPage,
    setFilters,
    showFilter,
    sort,
  } = useListContext(props);
  const hasFilterValues = !isEqual(filterValues, {});
  const validSavedQueries = extractValidSavedQueries(savedQueries);
  const currentQuery = { filter: filterValues, sort, perPage, displayedFilters };
  const hasSavedCurrentQuery = validSavedQueries.some((savedQuery) =>
    isEqual(savedQuery.value, currentQuery),
  );
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorEl = useRef<HTMLElement>();

  if (filters === undefined) {
    throw new Error('The <FilterButton> component requires the <List filters> prop to be set');
  }

  const hiddenFilters = filters.filter(
    (filterElement) =>
      !filterElement.props.alwaysOn &&
      !displayedFilters[filterElement.props.source] &&
      typeof get(filterValues, filterElement.props.source) === 'undefined',
  );

  const showSearch = hiddenFilters.length > FILTER_SEARCH_THRESHOLD;
  const isSearching = showSearch && search.trim() !== '';

  const getSearchTexts = (filterElement: ReactElement) => {
    const { label, source } = filterElement.props as { label?: unknown; source: string };
    // A non-string label (e.g. a React element) cannot be matched, fall back to the source.
    if (label !== undefined && typeof label !== 'string') {
      return [source];
    }

    return [
      translationToText(translate(...getFieldLabelTranslationArgs({ label, resource, source }))),
      source,
    ];
  };

  // Same fuzzy matching as the command palette: `uid` finds "User ID", best matches first.
  const visibleFilters = isSearching
    ? fuzzyFilter(hiddenFilters, search, getSearchTexts)
    : hiddenFilters;

  const handleClickButton = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // This prevents ghost click.
    event.preventDefault();
    setOpen(true);
    anchorEl.current = event.currentTarget;
  }, []);

  const handleRequestClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleShow = useCallback(
    ({ source, defaultValue }: { source: string; defaultValue: unknown }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue);
      // We have to fallback to imperative code because the new FilterFormInput
      // has no way of knowing it has just been displayed (and thus that it should focus its input)
      setTimeout(() => {
        const inputElement = document.querySelector<HTMLInputElement>(`input[name='${source}']`);
        inputElement?.focus();
      }, 50);
      setOpen(false);
    },
    [showFilter],
  );

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Escape and Tab close the menu, let MUI handle them.
    if (event.key === 'Escape' || event.key === 'Tab') {
      return;
    }

    // Every other key stays in the field: MUI's MenuList type-ahead would otherwise move the focus
    // to the first item starting with the typed letter.
    event.stopPropagation();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.currentTarget
        .closest('[role="menu"]')
        ?.querySelector<HTMLElement>('.new-filter-item:not(.Mui-disabled)')
        ?.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const firstFilter = visibleFilters.find((filterElement) => !filterElement.props.disabled);

      if (firstFilter) {
        handleShow({
          source: firstFilter.props.source,
          defaultValue: firstFilter.props.defaultValue,
        });
      }
    }
  };

  // add query dialog state
  const [addSavedQueryDialogOpen, setAddSavedQueryDialogOpen] = useState(false);
  const hideAddSavedQueryDialog = (): void => {
    setAddSavedQueryDialogOpen(false);
  };
  const showAddSavedQueryDialog = (): void => {
    setOpen(false);
    setAddSavedQueryDialogOpen(true);
  };

  // remove query dialog state
  const [removeSavedQueryDialogOpen, setRemoveSavedQueryDialogOpen] = useState(false);
  const hideRemoveSavedQueryDialog = (): void => {
    setRemoveSavedQueryDialogOpen(false);
  };
  const showRemoveSavedQueryDialog = (): void => {
    setOpen(false);
    setRemoveSavedQueryDialogOpen(true);
  };

  if (hiddenFilters.length === 0 && validSavedQueries.length === 0 && !hasFilterValues) {
    return null;
  }

  return (
    <Root className={className} {...sanitizeRestProps(rest)}>
      <RaButton
        className="add-filter"
        label="ra.action.add_filter"
        aria-haspopup="true"
        onClick={handleClickButton}
        data-testid="BoExternalFilterButton_AddFilterButton">
        <ContentFilter />
      </RaButton>
      <Menu
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleRequestClose}
        // oxlint-disable-next-line jsx-a11y/no-autofocus -- with a search field, the field keeps the focus instead of the first item or the list.
        autoFocus={!showSearch}
        disableAutoFocusItem={showSearch}
        MenuListProps={{ disablePadding: true }}
        TransitionProps={{ onExited: () => setSearch('') }}>
        {showSearch && (
          <SearchHeader>
            <TextField
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- search field should be focused to make searching faster
              autoFocus
              fullWidth
              size="small"
              margin="none"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={translate('resources.bo_common.filters.search_placeholder')}
              inputProps={{
                'aria-label': translate('resources.bo_common.filters.search_placeholder'),
              }}
              data-testid="BoExternalFilterButton_SearchInput"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 0,
                },
              }}
            />
          </SearchHeader>
        )}
        {visibleFilters.map((filterElement, index) => (
          <FilterButtonMenuItem
            key={filterElement.props.source}
            filter={filterElement}
            resource={resource}
            onShow={handleShow}
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- don't autofocus by default
            autoFocus={!showSearch && index === 0}
          />
        ))}
        {isSearching && visibleFilters.length === 0 && (
          <MenuItem disabled data-testid="BoExternalFilterButton_NoMatchItem">
            {translate('resources.bo_common.filters.no_match', { query: search.trim() })}
          </MenuItem>
        )}
        {!isSearching &&
          validSavedQueries.map((savedQuery, index) =>
            isEqual(savedQuery.value, currentQuery) ? (
              <MenuItem
                onClick={showRemoveSavedQueryDialog}
                key={index}
                data-testid="BoExternalFilterButton_RemoveSavedQueryItem">
                {translate('ra.saved_queries.remove_label_with_name', {
                  _: 'Remove query "%{name}"',
                  name: savedQuery.label,
                })}
              </MenuItem>
            ) : (
              <MenuItem
                onClick={(): void => {
                  navigate({ search: buildSavedQuerySearch(savedQuery) });
                  setOpen(false);
                }}
                key={index}
                data-testid="BoExternalFilterButton_SavedQueryItem">
                {savedQuery.label}
              </MenuItem>
            ),
          )}
        {!isSearching && hasFilterValues && !hasSavedCurrentQuery && !disableSaveQuery && (
          <MenuItem
            onClick={showAddSavedQueryDialog}
            data-testid="BoExternalFilterButton_SaveQueryItem">
            {translate('ra.saved_queries.new_label', {
              _: 'Save current query...',
            })}
          </MenuItem>
        )}
        {!isSearching && hasFilterValues && (
          <MenuItem
            onClick={() => {
              setFilters({}, {}, false);
              setOpen(false);
            }}
            data-testid="BoExternalFilterButton_RemoveAllFiltersItem">
            {translate('ra.action.remove_all_filters', {
              _: 'Remove all filters',
            })}
          </MenuItem>
        )}
      </Menu>
      {!disableSaveQuery && (
        <>
          <AddSavedQueryDialog open={addSavedQueryDialogOpen} onClose={hideAddSavedQueryDialog} />
          <RemoveSavedQueryDialog
            open={removeSavedQueryDialogOpen}
            onClose={hideRemoveSavedQueryDialog}
          />
        </>
      )}
    </Root>
  );
};

const sanitizeRestProps = ({
  displayedFilters: _displayedFilters,
  filterValues: _filterValues,
  showFilter: _showFilter,
  resource: _resource,
  ...rest
}: Omit<FilterButtonProps, 'filters' | 'className' | 'disableSaveQuery'>) => rest;

const Root = styled('div', {
  name: 'RaFilterButton',
  overridesResolver: (_props, styles) => styles.root,
})({
  display: 'inline-block',
});

const SearchHeader = styled(ListSubheader)(({ theme }) => ({
  padding: theme.spacing(0),
  lineHeight: 'normal',
}));
